import { create } from "zustand";
import { persist } from "zustand/middleware";
import { MedspaPackage } from "@/views/online-store/onlineStoreView";

type OnlineStorePackagesStore = {
  packagesQuantity: Record<string, number>;
  purchaseSource: { id: string; source: string } | undefined;
  changePackageQuantity: (packageId: string, change: 1 | -1) => void;
  clearExpiredPackages: (medspaPackages: MedspaPackage[]) => void;
  clearAllPackages: () => void;
  setPackageQuantity: (packageId: string, quantity: number) => void;
  getPackageQuantity: (packageId: string) => number;
  addPackage: (packageId: string) => void;
  removePackage: (packageId: string) => void;
  setPurchaseSource: (source: { id: string; source: string }) => void;
  getPurchaseSource: () => string | undefined;
};

const createOnlineStorePackagesStore = (medspaId: string) =>
  create<OnlineStorePackagesStore>()(
    persist(
      (set, get) => ({
        packagesQuantity: {},
        purchaseSource: undefined,
        changePackageQuantity(packageId: string, change: 1 | -1) {
          const currentPackages = get().packagesQuantity;

          if (currentPackages[packageId] === 1 && change === -1) {
            const newPackages = { ...currentPackages };
            delete newPackages[packageId];
            set({ packagesQuantity: newPackages });
          } else {
            set({
              packagesQuantity: {
                ...currentPackages,
                [packageId]: (currentPackages[packageId] || 0) + change,
              },
            });
          }
        },
        setPackageQuantity(packageId: string, quantity: number) {
          set({
            packagesQuantity: {
              ...get().packagesQuantity,
              [packageId]: Math.abs(Math.floor(quantity)),
            },
          });
        },
        getPackageQuantity(packageId: string) {
          return get().packagesQuantity[packageId] || 0;
        },
        addPackage(packageId: string) {
          set({
            packagesQuantity: {
              ...get().packagesQuantity,
              [packageId]: 1,
            },
          });
        },
        removePackage(packageId: string) {
          const newPackages = { ...get().packagesQuantity };
          delete newPackages[packageId];
          set({ packagesQuantity: newPackages });
        },
        clearExpiredPackages(medspaPackages: MedspaPackage[]) {
          const currentPackages = { ...get().packagesQuantity };

          for (const packageId of Object.keys(currentPackages)) {
            if (!medspaPackages.find((p) => p.id === packageId)) {
              delete currentPackages[packageId];
            }
          }

          set({ packagesQuantity: currentPackages });
        },
        clearAllPackages() {
          set({ packagesQuantity: {}, purchaseSource: undefined });
        },
        setPurchaseSource(source: { id: string; source: string }) {
          set({ purchaseSource: source });
        },
        getPurchaseSource() {
          // If the package was suggested and it's still in the packagesData, use the source from the suggested package
          const { packagesQuantity, purchaseSource } = get();

          if (!purchaseSource) return undefined;
          return packagesQuantity[purchaseSource.id]
            ? purchaseSource.source
            : undefined;
        },
      }),
      {
        name: `online-store-packages-${medspaId}`,
      }
    )
  );

const storeCache: Record<
  string,
  ReturnType<typeof createOnlineStorePackagesStore>
> = {};

const useOnlineStorePackagesStore = (medspaId: string) => {
  if (!storeCache[medspaId]) {
    storeCache[medspaId] = createOnlineStorePackagesStore(medspaId);
  }

  return storeCache[medspaId];
};

export default useOnlineStorePackagesStore;
